/**
 * Утилиты для расчета цен пакетов и апгрейдов
 * 
 * Пример использования:
 * const pricePoints = extractPricePoints(tariffs) // Извлекает общие цены пакетов
 * const upgradePricePoints = extractUpgradePricePoints(tariffs) // Извлекает общие цены апгрейдов
 * 
 * // Для количества 100 документов - будет использован тариф "Стандарт" (320000 копеек за пакет)
 * const pricePerDoc = calculatePricePerDocument(100, pricePoints) // 32 рубля за документ
 * const upgradePrices = calculateUpgradePrices(100, upgradePricePoints) // 9 рублей за апгрейд
 * 
 * // Для количества 150 документов - интерполяция общей стоимости пакета между "Стандарт" (100) и "Бизнес" (500)
 * // Затем деление на количество документов для получения цены за документ
 * // Если количество больше максимального тарифа (например, 2000 документов)
 * // Будет использована цена самого большого тарифа
 */

export interface PricePoint {
    size: number
    price: number
}

export interface UpgradePricePoint {
    size: number
    upgradeId: string
    price: number
}

/**
 * Рассчитывает общую стоимость пакета через интерполяцию между соседними тарифами
 * @param currentSize - текущий размер пакета
 * @param pricePoints - массив контрольных точек с размерами и общими ценами пакетов
 * @returns интерполированная общая стоимость пакета
 */
export function calculateAveragePackagePrice(currentSize: number, pricePoints: PricePoint[]): number {
    if (pricePoints.length === 0) {
        return 0
    }

    // Сортируем точки по размеру
    const sortedPoints = [...pricePoints].sort((a, b) => a.size - b.size)
    
    // Находим максимальный размер
    const maxSize = sortedPoints[sortedPoints.length - 1].size
    
    // Если количество больше максимального тарифа, используем цену за документ из самого большого тарифа
    if (currentSize > maxSize) {
        const maxTariff = sortedPoints[sortedPoints.length - 1]
        const pricePerDocument = maxTariff.price / maxTariff.size
        return pricePerDocument * currentSize
    }
    
    // Если количество меньше минимального тарифа, используем самый маленький
    const minSize = sortedPoints[0].size
    if (currentSize < minSize) {
        return sortedPoints[0].price
    }
    
    // Находим две соседние точки для интерполяции
    let lowerPoint: PricePoint | null = null
    let upperPoint: PricePoint | null = null

    for (let i = 0; i < sortedPoints.length; i++) {
        if (sortedPoints[i].size <= currentSize) {
            lowerPoint = sortedPoints[i]
        }
        if (sortedPoints[i].size >= currentSize && !upperPoint) {
            upperPoint = sortedPoints[i]
            break
        }
    }

    // Если точное совпадение с одним из тарифов
    if (lowerPoint && lowerPoint.size === currentSize) {
        return lowerPoint.price
    }
    
    if (upperPoint && upperPoint.size === currentSize) {
        return upperPoint.price
    }

    // Если есть обе точки, интерполируем между ними
    if (lowerPoint && upperPoint) {
        const ratio = (currentSize - lowerPoint.size) / (upperPoint.size - lowerPoint.size)
        return lowerPoint.price + (upperPoint.price - lowerPoint.price) * ratio
    }

    // Если есть только одна точка, используем её
    if (lowerPoint) {
        return lowerPoint.price
    }
    
    if (upperPoint) {
        return upperPoint.price
    }

    return 0
}

/**
 * Рассчитывает цену за документ для заданного размера пакета
 * @param currentSize - текущий размер пакета
 * @param pricePoints - массив контрольных точек с размерами и общими ценами пакетов (в копейках)
 * @returns цена за документ (в рублях)
 */
export function calculatePricePerDocument(currentSize: number, pricePoints: PricePoint[]): number {
    const totalPackagePrice = calculateAveragePackagePrice(currentSize, pricePoints)
    return totalPackagePrice / currentSize / 100
}

/**
 * Рассчитывает среднюю цену апгрейда на основе всех тарифов
 * @param currentSize - текущий размер пакета
 * @param upgradePricePoints - массив контрольных точек апгрейдов (цены в копейках)
 * @returns объект с средними ценами апгрейдов (цены в рублях)
 */
export function calculateUpgradePrices(
    currentSize: number, 
    upgradePricePoints: UpgradePricePoint[]
): { [upgradeId: string]: { price: number, displayText: string } } {
    // Группируем точки по ID апгрейда
    const upgradeGroups = new Map<string, PricePoint[]>()
    
    upgradePricePoints.forEach(point => {
        if (!upgradeGroups.has(point.upgradeId)) {
            upgradeGroups.set(point.upgradeId, [])
        }
        upgradeGroups.get(point.upgradeId)!.push({
            size: point.size,
            price: point.price
        })
    })

    const result: { [upgradeId: string]: { price: number, displayText: string } } = {}

    // Рассчитываем среднюю цену для каждого апгрейда
    upgradeGroups.forEach((pricePoints, upgradeId) => {
        const averagePrice = calculateAveragePackagePrice(currentSize, pricePoints)
        const pricePerDocument = averagePrice / currentSize
        const roundedPrice = Math.round(pricePerDocument)
        
        result[upgradeId] = {
            price: roundedPrice / 100, // Конвертируем в рубли
            displayText: `${roundedPrice / 100} ₽ за документ`
        }
    })

    return result
}

/**
 * Применяет скидку к цене
 * @param price - оригинальная цена в копейках
 * @param discount - скидка в процентах (0-100)
 * @returns цена со скидкой в копейках
 */
export function applyDiscountToPrice(price: number, discount: number): number {
    if (discount <= 0) {
        return price
    }
    return Math.round(price * (1 - discount / 100))
}

/**
 * Восстанавливает оригинальную цену из цены со скидкой
 * @param discountedPrice - цена со скидкой
 * @param discount - скидка в процентах (0-100)
 * @returns оригинальная цена (без скидки)
 */
export function restoreOriginalPrice(discountedPrice: number, discount: number): number {
    if (discount <= 0) {
        return discountedPrice
    }
    return Math.round(discountedPrice / (1 - discount / 100))
}

/**
 * Вычисляет скидку в процентах на основе оригинальной цены и цены со скидкой
 * @param originalPrice - оригинальная цена (без скидки)
 * @param discountedPrice - цена со скидкой
 * @returns скидка в процентах (0-100)
 */
export function calculateDiscountPercent(originalPrice: number, discountedPrice: number): number {
    if (originalPrice <= 0 || discountedPrice >= originalPrice) {
        return 0
    }
    return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)
}

/**
 * Извлекает контрольные точки цен из данных тарифов БЕЗ применения скидки
 * Используется для расчета оригинальной цены индивидуального тарифа
 * @param tariffs - массив тарифов с prepaid_documents_count и price
 * @returns массив контрольных точек для расчета общей стоимости пакета (оригинальные цены)
 */
export function extractPricePointsWithoutDiscount(tariffs: any[]): PricePoint[] {
    const pricePoints: PricePoint[] = []
    
    tariffs.forEach(tariff => {
        if (tariff.prepaid_documents_count && tariff.price) {
            pricePoints.push({
                size: tariff.prepaid_documents_count,
                price: tariff.price // Используем оригинальную цену без скидки
            })
        }
    })

    return pricePoints
}

/**
 * Извлекает контрольные точки цен из данных тарифов
 * Учитывает скидки для тарифов package_v2
 * @param tariffs - массив тарифов с prepaid_documents_count и price
 * @returns массив контрольных точек для расчета общей стоимости пакета (цены со скидкой)
 */
export function extractPricePoints(tariffs: any[]): PricePoint[] {
    const pricePoints: PricePoint[] = []
    
    tariffs.forEach(tariff => {
        if (tariff.prepaid_documents_count && tariff.price) {
            // Применяем скидку к цене, если она есть
            const discount = tariff.discount || 0
            const finalPrice = applyDiscountToPrice(tariff.price, discount)
            
            pricePoints.push({
                size: tariff.prepaid_documents_count,
                price: finalPrice // Используем цену со скидкой
            })
        }
    })

    return pricePoints
}

/**
 * Извлекает контрольные точки цен апгрейдов из данных тарифов
 * @param tariffs - массив тарифов с upgrade_additional_price
 * @returns массив контрольных точек для расчета цен апгрейдов
 */
export function extractUpgradePricePoints(tariffs: any[]): UpgradePricePoint[] {
    const upgradePricePoints: UpgradePricePoint[] = []
    
    tariffs.forEach(tariff => {
        if (tariff.prepaid_documents_count && tariff.upgrade_additional_price) {
            Object.entries(tariff.upgrade_additional_price).forEach(([upgradeId, price]) => {
                upgradePricePoints.push({
                    size: tariff.prepaid_documents_count,
                    upgradeId: upgradeId,
                    price: price as number // Используем общую цену апгрейда
                })
            })
        }
    })

    return upgradePricePoints
}
